import React, { useEffect, useState, useRef, useCallback } from 'react';
import { collection, query, where, getDocs, deleteDoc, doc, addDoc, getDoc } from 'firebase/firestore';
import { ref, getDownloadURL, deleteObject, uploadBytes } from 'firebase/storage';
import { firestore, storage } from '../config/firebase';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
// import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import PDFViewer from './PDFViewer';
import { ExtractedData, extractPDFData } from '../utils/pdfUtils';
import { DocumentIcon, EyeIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import UploadProgressModal, { FileUploadStatus } from './UploadProgressModal';
import DeleteProgressModal, { FileDeleteStatus } from './DeleteProgressModal';

import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { ArrowUpDown, ChevronDown, DownloadIcon, MoreHorizontal } from "lucide-react"

import { Button } from "./ui/button"
import { Checkbox } from "./ui/checkbox"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"
import { Input } from "./ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table"
import {
  Breadcrumb,
  // BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "./ui/breadcrumb"
import { AlertDialogDelete } from './AlertDialogDelete';
import { ButtonIcon } from '@radix-ui/react-icons';

export interface Document {
  id: string;
  filename: string;
  size: number;
  uploadedAt: Date;
  url?: string;
  storageRef: string;
  metadata?: ExtractedData;
}

const interfaceMappings: { [key: string]: string } = {
  "metadata.referenceNumber": "Reference",
  "metadata.clientName": "Client Name",
  "metadata.amount": "Amount",
  "metadata.date": "Date",
  "metadata.documentType": "Document Type",
  "metadata.authNumber": "Auth Number",
  "metadata.remitent": "Remitent",
  "metadata.originCountry": "Origin Country",
  "metadata.clientIdType": "Client ID Type"
}

// Used for typing upload errors in the file upload process
type DocumentError = Error & Document;

type ExtendedColumnDef = ColumnDef<Document> & {
  show?: boolean;
};

const DocumentList = (): React.ReactElement => {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState({})

  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [uploadQueue, setUploadQueue] = useState<File[]>([]);
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);
  const dropAreaRef = useRef<HTMLDivElement>(null);
  
  // Upload progress modal state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [fileStatuses, setFileStatuses] = useState<FileUploadStatus[]>([]);
  
  // Delete progress modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [fileDeleteStatuses, setFileDeleteStatuses] = useState<FileDeleteStatus[]>([]);
  const [deleteProgress, setDeleteProgress] = useState(0);
  const [isProcessingDeleteQueue, setIsProcessingDeleteQueue] = useState(false);
  const [deleteQueue, setDeleteQueue] = useState<Document[]>([]);
  const processDeleteQueueRef = useRef<(() => void) | null>(null);
  
  // Function reference to avoid circular dependency
  const processQueueRef = useRef<(() => void) | null>(null);
  const [groupName, setGroupName] = useState('');
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const { groupId } = useParams<{ groupId: string }>();
  const { currentUser } = useAuth();
  const [userRole, setUserRole] = useState<string>('');
  // const navigate = useNavigate();
  
  // Process files from the queue one by one with rate limiting
  const processUploadQueue = useCallback(async () => {
    console.log('Processing queue, files remaining:', uploadQueue.length);
    
    // Make a local copy of the queue to avoid state update issues
    const currentQueue = [...uploadQueue];
    
    // Check if there are files to process
    if (currentQueue.length === 0) {
      console.log('Queue empty, stopping processing');
      setIsProcessingQueue(false);
      setUploading(false);
      setUploadProgress(0);
      setTotalFiles(0);
      return;
    }

    // Check if user is still logged in
    if (!currentUser) {
      toast.error('You must be logged in to upload files');
      setIsProcessingQueue(false);
      setUploading(false);
      setUploadProgress(0);
      setTotalFiles(0);
      setUploadQueue([]);
      setFileStatuses(prev => prev.map(status => (
        status.status === 'pending' ? { ...status, status: 'error', error: 'User not logged in' } : status
      )));
      return;
    }
    
    // We're already in processing state at this point
    setUploading(true);

    // Take the first file from the queue
    const file = currentQueue[0];
    const remainingQueue = currentQueue.slice(1);
    
    // Update the queue state - but AFTER we've taken the current file
    console.log('Processing file:', file.name, 'Remaining:', remainingQueue.length);
    setUploadQueue(remainingQueue);

    // Update progress
    const processedFiles = totalFiles - remainingQueue.length;
    const progressPercentage = Math.round((processedFiles / totalFiles) * 100);
    setUploadProgress(progressPercentage);
    
    // Calculate and update the total progress for the modal
    const completedFiles = fileStatuses.filter(f => f.status === 'success').length;
    const inProgressFiles = fileStatuses.filter(f => f.status === 'uploading').length;
    const pendingFiles = fileStatuses.filter(f => f.status === 'pending').length;
    const errorFiles = fileStatuses.filter(f => f.status === 'error').length;
    
    console.log(`Progress stats: Total: ${totalFiles}, Completed: ${completedFiles}, In Progress: ${inProgressFiles}, Pending: ${pendingFiles}, Errors: ${errorFiles}`);

    // Find the file status entry for this file
    const fileStatusIndex = fileStatuses.findIndex(status => status.name === file.name && status.status === 'pending');
    
    if (fileStatusIndex !== -1) {
      // Update status to uploading
      setFileStatuses(prev => {
        const updated = [...prev];
        updated[fileStatusIndex] = { ...updated[fileStatusIndex], status: 'uploading', progress: 0 };
        return updated;
      });
    }

    try {
      // Upload file to Firebase Storage
      const storageRef = ref(storage, `files/${currentUser?.uid || 'anonymous'}/${groupId}/${Date.now()}_${file.name}`);
      
      // Upload with progress tracking
      const uploadTask = uploadBytes(storageRef, file);
      
      // Update file status progress periodically during upload
      // Note: Firebase's uploadBytes doesn't provide progress events, so we'll simulate progress
      let progress = 0;
      const progressInterval = setInterval(() => {
        progress += 10;
        if (progress <= 90) {
          // Update the file status progress
          if (fileStatusIndex !== -1) {
            setFileStatuses(prev => {
              const updated = [...prev];
              updated[fileStatusIndex] = { ...updated[fileStatusIndex], progress };
              return updated;
            });
          }
        } else {
          clearInterval(progressInterval);
        }
      }, 100); // Faster progress updates

      await uploadTask;
      clearInterval(progressInterval);

      // Get download URL
      const downloadUrl = await getDownloadURL(storageRef);

      // Extract PDF data
      let metadata = {};
      if (file.type === 'application/pdf') {
        try {
          metadata = await extractPDFData(downloadUrl);
        } catch (error) {
          console.error('Error extracting PDF data:', error);
        }
      }

      // Create document in Firestore
      const docRef = await addDoc(collection(firestore, 'documents'), {
        filename: file.name,
        size: file.size,
        uploadedAt: new Date(),
        userId: currentUser?.uid || '',
        groupId: groupId,
        storageRef: storageRef.fullPath,
        url: downloadUrl,
        metadata: metadata
      });

      // Get the newly created document
      const newDocSnapshot = await getDoc(docRef);
      const newDoc = {
        id: newDocSnapshot.id,
        ...newDocSnapshot.data()
      } as Document;

      // Update the local state with the new document
      setDocuments(prevDocs => [newDoc, ...prevDocs]);

      // Update file status to success
      if (fileStatusIndex !== -1) {
        setFileStatuses(prev => {
          const updated = [...prev];
          updated[fileStatusIndex] = { ...updated[fileStatusIndex], status: 'success', progress: 100 };
          return updated;
        });
      }

      toast.success(`Uploaded ${file.name}`);

      console.log('Upload successful for:', file.name);
      // Process the next file after a short delay
      setTimeout(() => {
        if (processQueueRef.current) {
          console.log('Calling processQueueRef.current() for next file');
          processQueueRef.current();
        } else {
          console.error('processQueueRef.current is null');
        }
      }, 5000);

    } catch (error) {
      console.error('Error uploading file:', error);
      
      // Update file status to error
      if (fileStatusIndex !== -1) {
        setFileStatuses(prev => {
          const updated = [...prev];
          updated[fileStatusIndex] = { 
            ...updated[fileStatusIndex], 
            status: 'error', 
            error: error instanceof Error ? error.message : 'Unknown error during upload' 
          };
          return updated;
        });
      }
      
      toast.error(`Failed to upload ${file.name}`);
      
      // Continue with the next file after waiting
      console.log('Upload failed, continuing to next file');
      setTimeout(() => {
        if (processQueueRef.current) {
          console.log('Calling processQueueRef.current() after error');
          processQueueRef.current();
        } else {
          console.error('processQueueRef.current is null after error');
        }
      }, 500);
    }
  }, [uploadQueue, totalFiles, currentUser, groupId, fileStatuses]);

  const fetchUserRole = useCallback(async () => {
    if (currentUser) {
      const userDoc = await getDocs(query(collection(firestore, 'users'), where('email', '==', currentUser.email)));
      if (!userDoc.empty) {
        setUserRole(userDoc.docs[0].data().role);
      }
    }
  }, [currentUser]);

  const fetchGroupDetails = useCallback(async () => {
    if (groupId) {
      try {
        const groupRef = doc(firestore, 'documentGroups', groupId);
        const groupDoc = await getDoc(groupRef);
        if (groupDoc.exists()) {
          setGroupName(groupDoc.data().name);
        }
      } catch (error) {
        console.error('Error fetching group details:', error);
      }
    }
  }, [groupId]);

  const fetchDocuments = useCallback(async () => {
    if (!groupId || !currentUser) return;

    setLoading(true);
    try {
      const q = query(
        collection(firestore, 'documents'),
        where('groupId', '==', groupId)
      );
      const querySnapshot = await getDocs(q);
      const docs = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          uploadedAt: data.uploadedAt?.toDate?.() || data.uploadedAt || new Date(),
        } as Document;
      });

      setDocuments(docs.sort((a, b) => {
        const dateA = a.uploadedAt instanceof Date ? a.uploadedAt : new Date(a.uploadedAt);
        const dateB = b.uploadedAt instanceof Date ? b.uploadedAt : new Date(b.uploadedAt);
        return dateB.getTime() - dateA.getTime();
      }));
    } catch (error) {
      console.error('Error fetching documents:', error);
      alert('Error fetching documents. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [groupId, currentUser]);

  // Add files to upload queue
  const addFilesToQueue = useCallback((files: File[]) => {
    if (!currentUser) {
      toast.error('Please sign in to upload files');
      return;
    }

    console.log('Adding', files.length, 'files to queue');
    
    // Create file status entries for each file
    const newFileStatuses = files.map(file => ({
      id: `${Date.now()}-${file.name}`,
      name: file.name,
      status: 'pending' as const,
      progress: 0
    }));
    
    // Reset processing state if needed
    if (isProcessingQueue) {
      setIsProcessingQueue(false);
    }
    
    // Open the upload progress modal
    setIsUploadModalOpen(true);
    
    // Update state in a single batch to avoid race conditions
    setFileStatuses(prev => [...prev, ...newFileStatuses]);
    setTotalFiles(prevTotal => prevTotal + files.length);
    setUploadQueue(prevQueue => [...prevQueue, ...files]);
    
    // Manually start processing after a short delay to ensure state is updated
    setTimeout(() => {
      if (processQueueRef.current) {
        console.log('Manually starting upload queue processing');
        setIsProcessingQueue(true);
        processQueueRef.current();
      } else {
        console.error('processQueueRef.current is null in addFilesToQueue');
      }
    }, 200);
    
    console.log('Files added to queue, manual processing will start soon');
  }, [currentUser, isProcessingQueue]);

  // Monitor the upload queue and start processing when needed
  useEffect(() => {
    console.log('Upload queue monitoring effect, queue length:', uploadQueue.length);
    console.log('Upload queue contents:', uploadQueue);
    console.log('isProcessingQueue:', isProcessingQueue);
    console.log('processQueueRef.current exists:', processQueueRef.current ? 'yes' : 'no');
    
    // Start processing the queue if there are files and we're not already processing
    if (uploadQueue.length > 0 && !isProcessingQueue) {
      console.log('Starting queue processing from queue monitoring effect');
      setIsProcessingQueue(true);
      
      // Use setTimeout to ensure this runs after processQueueRef is set
      setTimeout(() => {
        console.log('Inside setTimeout, processQueueRef.current exists:', processQueueRef.current ? 'yes' : 'no');
        if (processQueueRef.current) {
          console.log('Calling processQueueRef.current()');
          processQueueRef.current();
        } else {
          console.error('processQueueRef.current is null, cannot process upload queue');
          // Try to recover by setting isProcessingQueue to false
          setIsProcessingQueue(false);
        }
      }, 100);
    }
  }, [uploadQueue, isProcessingQueue]);
  
  // Monitor the delete queue and start processing when needed
  useEffect(() => {
    console.log('Delete queue monitoring effect, queue length:', deleteQueue.length);
    console.log('Delete queue contents:', deleteQueue);
    console.log('isProcessingDeleteQueue:', isProcessingDeleteQueue);
    console.log('processDeleteQueueRef.current exists:', processDeleteQueueRef.current ? 'yes' : 'no');
    
    // Start processing the queue if there are files and we're not already processing
    if (deleteQueue.length > 0 && !isProcessingDeleteQueue) {
      console.log('Starting delete queue processing from queue monitoring effect');
      setIsProcessingDeleteQueue(true);
      
      // Use setTimeout to ensure this runs after processDeleteQueueRef is set
      setTimeout(() => {
        console.log('Inside setTimeout, processDeleteQueueRef.current exists:', processDeleteQueueRef.current ? 'yes' : 'no');
        if (processDeleteQueueRef.current) {
          console.log('Calling processDeleteQueueRef.current()');
          processDeleteQueueRef.current();
        } else {
          console.error('processDeleteQueueRef.current is null, cannot process delete queue');
          // Try to recover by setting isProcessingDeleteQueue to false
          setIsProcessingDeleteQueue(false);
        }
      }, 100);
    }
  }, [deleteQueue, isProcessingDeleteQueue]);

  // Fetch initial data
  useEffect(() => {
    fetchUserRole();
    fetchGroupDetails();
    fetchDocuments();
  }, [fetchUserRole, fetchGroupDetails, fetchDocuments]);

  const columns: ExtendedColumnDef[] = [

    // Checkbox
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },

    // Document name
    {
      accessorKey: "filename",
      id: "filename",
      header: "MTCN document",
      cell: ({ row }) => (
        <>
          {row.getValue("filename") && (
            <div className="text-sm font-medium text-gray-900">{row.getValue(("metadata.clientName"))}</div>
          )}
          <div className="text-xs text-gray-500">{row.getValue("filename")}</div>
        </>
      ),
    },

    // Reference
    {
      accessorKey: "metadata.referenceNumber",
      id: "metadata.referenceNumber",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Reference
            <ArrowUpDown />
          </Button>
        )
      },
      cell: ({ row }) => <div>{row.getValue("metadata.referenceNumber")}</div>,
      show: true,
    },

    // Client Name
    {
      accessorKey: "metadata.clientName",
      id: "metadata.clientName",
      enableHiding: true,
      show: true,
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Client Name
            <ArrowUpDown />
          </Button>
        )
      },
      cell: ({ row }) => <div>{row.getValue("metadata.clientName")}</div>,
    },

    // Remitent
    {
      accessorKey: "metadata.remitent",
      id: "metadata.remitent",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Remitent
            <ArrowUpDown />
          </Button>
        )
      },
      cell: ({ row }) => <div>{row.getValue("metadata.remitent")}</div>,
    },

    // Date
    {
      accessorKey: "metadata.date",
      id: "metadata.date",
      header: () => <div className="text-center">Date</div>,
      cell: ({ row }) => {
        const dateValue = row.getValue("metadata.date");

        // Check if dateValue is null, undefined, or an empty object
        if (!dateValue || (typeof dateValue === 'object' && Object.keys(dateValue).length === 0)) {
          return <div className="text-right font-medium">-</div>;
        }

        try {
          // Explicitly convert to string first
          const dateString = dateValue.toString();
          const date = new Date(dateString);

          if (isNaN(date.getTime())) {
            return <div className="text-right font-medium">{dateString}</div>;
          }

          return <div className="text-right font-medium">{format(date, 'dd/MM/yy HH:mm:ss')}</div>;
        } catch (error) {
          console.error('Error formatting date:', error);
          return <div className="text-right font-medium">Invalid date {JSON.stringify(error)}</div>;
        }
      },
    },

    // Amount
    {
      accessorKey: "metadata.amount",
      id: "metadata.amount",
      header: () => <div className="text-right">Amount</div>,
      cell: ({ row }) => {
        const amount = parseFloat(row.getValue("metadata.amount"))

        // Format the amount as a dollar amount
        const formatted = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(amount)

        return <div className="text-right font-medium">{formatted}</div>
      },
    },

    // Auth Number
    {
      accessorKey: "metadata.authNumber",
      id: "metadata.authNumber",
      header: () => <div className="text-right">Auth Number</div>,
      cell: ({ row }) => {
        return <div className="text-right font-medium">{row.getValue("metadata.authNumber")}</div>
      },
    },

    // Actions
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => {
        const mtcn = row.original

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => setSelectedDocument(mtcn)}
              >
                <EyeIcon className="h-5 w-5" />
                View document
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  const documentUrl = mtcn.url || mtcn.storageRef || '';
                  handlePrint({ pdfUrl: documentUrl });
                }}>
                <DownloadIcon className="h-5 w-5" />
                Download document
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {userRole === 'admin' && (
                <AlertDialogDelete collectionName='documents' documentId={mtcn.id} buttonTitle='Delete document' onDocumentDeleted={fetchDocuments} />
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]

  const table = useReactTable({
    data: documents,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  })





  // Handle file input change
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    
    const files = Array.from(event.target.files);
    addFilesToQueue(files);
    
    // Reset the file input
    if (event.target.form) {
      event.target.form.reset();
    }
  };

  // Handle file drop
  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const files = Array.from(event.dataTransfer.files).filter(file => file.type === 'application/pdf');
      
      if (files.length === 0) {
        toast.error('Only PDF files are accepted');
        return;
      }
      
      addFilesToQueue(files);
    }
    
    if (dropAreaRef.current) {
      dropAreaRef.current.classList.remove('bg-indigo-50');
    }
  }, [addFilesToQueue]);

  // Handle drag events
  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (dropAreaRef.current) {
      dropAreaRef.current.classList.add('bg-indigo-50');
    }
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (dropAreaRef.current) {
      dropAreaRef.current.classList.remove('bg-indigo-50');
    }
  }, []);

  const handlePrint = ({pdfUrl}: {pdfUrl: string}) => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank')?.print();
    }
  };


  const formatDate = (date: Date | { seconds: number, nanoseconds: number } | any) => {
    if (!date) return 'N/A';

    // Handle Firestore Timestamp
    if (date && typeof date === 'object' && 'seconds' in date) {
      return format(new Date(date.seconds * 1000), 'MMM d, yyyy HH:mm');
    }

    // Handle regular Date object
    if (date instanceof Date) {
      return format(date, 'MMM d, yyyy HH:mm');
    }

    // Try to parse string date
    try {
      return format(new Date(date), 'MMM d, yyyy HH:mm');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  // Fetch documents when component mounts
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!groupId || !currentUser) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const q = query(
          collection(firestore, 'documents'),
          where('groupId', '==', groupId)
        );
        const querySnapshot = await getDocs(q);
        const docs = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            uploadedAt: data.uploadedAt?.toDate?.() || data.uploadedAt || new Date(),
          } as Document;
        });

        setDocuments(docs.sort((a, b) => {
          const dateA = a.uploadedAt instanceof Date ? a.uploadedAt : new Date(a.uploadedAt);
          const dateB = b.uploadedAt instanceof Date ? b.uploadedAt : new Date(b.uploadedAt);
          return dateB.getTime() - dateA.getTime();
        }));
      } catch (error) {
        console.error('Error fetching documents:', error);
        alert('Error fetching documents. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDocuments();
  }, [groupId, currentUser]);

  // Process files from the delete queue one by one
  const processDeleteQueue = useCallback(async () => {
    console.log('processDeleteQueue called, deleteQueue length:', deleteQueue.length);
    
    // Make a local copy of the queue to avoid state update issues
    const currentQueue = [...deleteQueue];
    console.log('Current queue copy:', currentQueue);
    
    // Check if there are files to process
    if (currentQueue.length === 0) {
      console.log('Delete queue empty, stopping processing');
      setIsProcessingDeleteQueue(false);
      setDeleteProgress(0);
      return;
    }

    // Check if user is still logged in
    if (!currentUser) {
      toast.error('You must be logged in to delete files');
      setIsProcessingDeleteQueue(false);
      setDeleteProgress(0);
      setDeleteQueue([]);
      setFileDeleteStatuses(prev => prev.map(status => (
        status.status === 'pending' ? { ...status, status: 'error', error: 'User not logged in' } : status
      )));
      return;
    }
    
    // Take the first file from the queue
    const document = currentQueue[0];
    const remainingQueue = currentQueue.slice(1);
    
    // Update the queue state
    console.log('Processing delete for file:', document.filename, 'Remaining:', remainingQueue.length);
    setDeleteQueue(remainingQueue);

    // Update progress
    const totalFiles = fileDeleteStatuses.length;
    const processedFiles = totalFiles - remainingQueue.length;
    const progressPercentage = Math.round((processedFiles / totalFiles) * 100);
    setDeleteProgress(progressPercentage);
    
    // Calculate and update the total progress for the modal
    const completedFiles = fileDeleteStatuses.filter(f => f.status === 'success').length;
    const inProgressFiles = fileDeleteStatuses.filter(f => f.status === 'deleting').length;
    const pendingFiles = fileDeleteStatuses.filter(f => f.status === 'pending').length;
    const errorFiles = fileDeleteStatuses.filter(f => f.status === 'error').length;
    
    console.log(`Delete progress stats: Total: ${totalFiles}, Completed: ${completedFiles}, In Progress: ${inProgressFiles}, Pending: ${pendingFiles}, Errors: ${errorFiles}`);

    // Find the file status entry for this file
    const fileStatusIndex = fileDeleteStatuses.findIndex(status => status.id === document.id && status.status === 'pending');
    
    if (fileStatusIndex !== -1) {
      // Update status to deleting
      setFileDeleteStatuses(prev => {
        const updated = [...prev];
        updated[fileStatusIndex] = { ...updated[fileStatusIndex], status: 'deleting', progress: 0 };
        return updated;
      });
    }

    try {
      // Update file status progress periodically during deletion
      let progress = 0;
      const progressInterval = setInterval(() => {
        progress += 20;
        if (progress <= 80) {
          // Update the file status progress
          if (fileStatusIndex !== -1) {
            setFileDeleteStatuses(prev => {
              const updated = [...prev];
              updated[fileStatusIndex] = { ...updated[fileStatusIndex], progress };
              return updated;
            });
          }
        } else {
          clearInterval(progressInterval);
        }
      }, 100);

      // Check user role from state
      if (userRole !== 'admin') {
        clearInterval(progressInterval);
        throw new Error('Only administrators can delete documents');
      }

      // Get the document reference
      const docRef = doc(firestore, 'documents', document.id);
      
      // Delete the file from storage if it exists
      if (document.storageRef) {
        const storageReference = ref(storage, document.storageRef);
        await deleteObject(storageReference);
      }
      
      // Delete the document from Firestore
      await deleteDoc(docRef);
      
      clearInterval(progressInterval);
      
      // Update the local state
      setDocuments(prevDocs => prevDocs.filter(doc => doc.id !== document.id));
      
      // Update file status to success
      if (fileStatusIndex !== -1) {
        setFileDeleteStatuses(prev => {
          const updated = [...prev];
          updated[fileStatusIndex] = { ...updated[fileStatusIndex], status: 'success', progress: 100 };
          return updated;
        });
      }

      toast.success(`Deleted ${document.filename}`);

      console.log('Delete successful for:', document.filename);
      // Process the next file after a short delay
      setTimeout(() => {
        if (processDeleteQueueRef.current) {
          console.log('Calling processDeleteQueueRef.current() for next file');
          processDeleteQueueRef.current();
        } else {
          console.error('processDeleteQueueRef.current is null');
        }
      }, 300);

    } catch (error) {
      console.error('Error deleting file:', error);
      
      // Update file status to error
      if (fileStatusIndex !== -1) {
        setFileDeleteStatuses(prev => {
          const updated = [...prev];
          updated[fileStatusIndex] = { 
            ...updated[fileStatusIndex], 
            status: 'error', 
            error: error instanceof Error ? error.message : 'Unknown error during deletion' 
          };
          return updated;
        });
      }
      
      toast.error(`Failed to delete ${document.filename}`);
      
      // Continue with the next file after waiting
      console.log('Delete failed, continuing to next file');
      setTimeout(() => {
        if (processDeleteQueueRef.current) {
          console.log('Calling processDeleteQueueRef.current() after error');
          processDeleteQueueRef.current();
        } else {
          console.error('processDeleteQueueRef.current is null after error');
        }
      }, 300);
    }
  }, [deleteQueue, currentUser, fileDeleteStatuses, userRole]);

  // Delete a single document
  const handleDelete = async (documentId: string, storageRef: string) => {
    // Check user role from state
    if (userRole !== 'admin') {
      alert('Only administrators can delete documents');
      return;
    }

    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await deleteDoc(doc(firestore, 'documents', documentId));
        const fileRef = ref(storage, storageRef);
        await deleteObject(fileRef);
        setDocuments(documents.filter((doc: Document) => doc.id !== documentId));
      } catch (error) {
        console.error('Error deleting document:', error);
        alert('Failed to delete document');
      }
    }
  };
  
  // Delete multiple documents
  const handleMultipleDelete = useCallback(() => {
    console.log('handleMultipleDelete called');
    
    if (!currentUser) {
      toast.error('You must be logged in to delete documents');
      return;
    }
    
    if (userRole !== 'admin') {
      toast.error('Only administrators can delete documents');
      return;
    }
    
    // Get selected documents
    const selectedDocIds = Object.keys(rowSelection);
    console.log('Selected document IDs:', selectedDocIds);
    
    if (selectedDocIds.length === 0) {
      toast.error('No documents selected for deletion');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete ${selectedDocIds.length} selected document(s)?`)) {
      console.log('Deletion confirmed by user');
      
      // Convert rowSelection indices to actual documents
      // rowSelection keys are indices in the table, not document IDs
      const selectedDocs = selectedDocIds.map(index => {
        // Get the row directly using the index string
        return table.getRow(index).original;
      });
      
      console.log('Selected docs to delete:', selectedDocs);
      
      // Create file status entries for each document
      const newFileDeleteStatuses = selectedDocs.map(doc => ({
        id: doc.id,
        name: doc.filename,
        status: 'pending' as const,
        progress: 0
      }));
      
      console.log('Created file delete statuses:', newFileDeleteStatuses);
      
      // Reset row selection first
      table.resetRowSelection();
      
      // Update all state in a single batch
      setFileDeleteStatuses(newFileDeleteStatuses);
      setIsDeleteModalOpen(true);
      setDeleteProgress(0);
      setIsProcessingDeleteQueue(false);
      
      // Add documents to the delete queue last
      console.log('Setting delete queue with docs:', selectedDocs);
      setDeleteQueue(selectedDocs);
      
      // Manually start processing after a short delay to ensure state is updated
      setTimeout(() => {
        if (processDeleteQueueRef.current) {
          console.log('Manually starting delete queue processing');
          setIsProcessingDeleteQueue(true);
          processDeleteQueueRef.current();
        } else {
          console.error('processDeleteQueueRef.current is null in handleMultipleDelete');
        }
      }, 200);
    }
  }, [currentUser, rowSelection, table, userRole]);

  // Set up the process queue references after the functions are defined
  useEffect(() => {
    console.log('Setting up process queue references');
    processQueueRef.current = processUploadQueue;
    processDeleteQueueRef.current = processDeleteQueue;
    console.log('processDeleteQueueRef.current is set');
  }, [processUploadQueue, processDeleteQueue]);

  // Utility function to format file sizes
const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Show loading spinner when data is being fetched
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (

    <div className="w-full">
      {/* Upload Progress Modal */}
      <UploadProgressModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        files={fileStatuses}
        totalProgress={uploadProgress}
      />
      
      <DeleteProgressModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        files={fileDeleteStatuses}
        totalProgress={deleteProgress}
      />
      
      <div className="flex flex-col items-start justify-between mb-4">
      <Breadcrumb className='mb-4'>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink href="/">Groups</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>{groupName}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center">
          Documents in <span className="ml-2 text-indigo-600">{groupName}</span>
        </h1>
        <p className="mt-2 text-sm text-gray-700">
          Manage documents in the {groupName} group
        </p>
      </div>
      <div className="flex items-center py-4">
        <Input
          placeholder="Filter documents"
          value={(table.getColumn("metadata.referenceNumber")?.getFilterValue() as string) ?? ""}
          onChange={(event) =>
            table.getColumn("metadata.referenceNumber")?.setFilterValue(event.target.value)
          }
          className="max-w-sm"
        />
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none flex items-center gap-2">
          {Object.keys(rowSelection).length > 0 && userRole === 'admin' && (
            <Button variant="destructive" onClick={handleMultipleDelete} className="mr-2">
              Delete Selected ({Object.keys(rowSelection).length})
            </Button>
          )}
          {!uploading && (<Button variant="outline" onClick={() => document.getElementById('file-upload')?.click()}>Upload files</Button>)}
          <input
            id="file-upload"
            name="file-upload"
            type="file"
            className="sr-only"
            onChange={handleFileUpload}
            multiple
            accept=".pdf"
            disabled={uploading}
          />
          {uploading && (
            <div className="mt-2 text-sm text-gray-500">
              Uploading files... {uploadProgress}% ({totalFiles - uploadQueue.length}/{totalFiles})
            </div>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="ml-auto">
              Columns <ChevronDown />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {interfaceMappings[column.id] || column.id}
                  </DropdownMenuCheckboxItem>
                )
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {/* File Drop Area */}
      <div 
        ref={dropAreaRef}
        className="mb-4 p-6 border-2 border-dashed border-gray-300 rounded-md text-center transition-colors duration-200 ease-in-out"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-1 text-sm text-gray-600">Drag and drop PDF files here</p>
        <p className="text-xs text-gray-500 mt-1">Files will be uploaded one by one (1 file every 5 seconds)</p>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-end space-x-2 py-4">
        <div className="flex-1 text-sm text-muted-foreground">
          {table.getFilteredSelectedRowModel().rows.length} of{" "}
          {table.getFilteredRowModel().rows.length} row(s) selected.
        </div>
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
      </div>

      {selectedDocument !== null && selectedDocument?.url && (
        <PDFViewer
          url={selectedDocument.url}
          filename={selectedDocument.filename}
          isOpen={true}
          onClose={() => setSelectedDocument(null)}
        />
      )}

    </div>

    // <div className="px-4 sm:px-6 lg:px-8">
    //   <Toaster position="top-right" />
    //   <div className="sm:flex sm:items-center">
    //     <div className="sm:flex-auto">
    //       <button
    //         onClick={() => navigate('/')}
    //         className="mb-4 inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
    //       >
    //         <ArrowLeftIcon className="mr-2 h-4 w-4" />
    //         Back to Groups
    //       </button>
    //       <h1 className="text-2xl font-semibold text-gray-900 flex items-center">
    //         Documents in <span className="ml-2 text-indigo-600">{groupName}</span>
    //       </h1>
    //       <p className="mt-2 text-sm text-gray-700">
    //         Manage documents in the {groupName} group
    //       </p>
    //     </div>
    //     <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
    //       <div className="mt-4">
    //         <label
    //           htmlFor="file-upload"
    //           className={`
    //             relative cursor-pointer rounded-md bg-white font-medium text-blue-600 
    //             focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500 
    //             focus-within:ring-offset-2 hover:text-blue-500
    //             ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
    //           `}
    //         >
    //           <span>Upload files</span>
    //           <input
    //             id="file-upload"
    //             name="file-upload"
    //             type="file"
    //             className="sr-only"
    //             onChange={handleFileUpload}
    //             multiple
    //             accept=".pdf"
    //             disabled={uploading}
    //           />
    //         </label>
    //         {uploading && (
    //           <div className="mt-2 text-sm text-gray-500">
    //             Uploading files...
    //           </div>
    //         )}
    //       </div>
    //     </div>
    //   </div>

    //   {documents.length === 0 ? (
    //     <div className="text-center py-12">
    //       <p className="text-gray-500">No documents in this group yet.</p>
    //     </div>
    //   ) : (
    //     <div className="mt-8 flow-root">
    //       <div className="overflow-x-auto">
    //         <table className="min-w-full divide-y divide-gray-200">
    //           <thead className="bg-gray-50">
    //             <tr>
    //               <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
    //                 Name
    //               </th>
    //               <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
    //                 Reference
    //               </th>
    //               <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
    //                 Date
    //               </th>
    //               <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
    //                 Remitent
    //               </th>
    //               <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
    //                 Amount
    //               </th>
    //               <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
    //                 Origin
    //               </th>
    //               <th scope="col" className="relative px-6 py-3">
    //                 <span className="sr-only">Actions</span>
    //               </th>
    //             </tr>
    //           </thead>
    //           <tbody className="bg-white divide-y divide-gray-200">
    //             {documents.map((doc) => (
    //               <tr key={doc.id} className="hover:bg-gray-50">
    //                 <td className="px-6 py-4 whitespace-nowrap">
    //                   <div className="flex items-center">
    //                     <DocumentIcon className="flex-shrink-0 h-5 w-5 text-gray-400" />
    //                     <div className="ml-4">
    //                       {doc.metadata?.clientName && (
    //                         <div className="text-sm font-medium text-gray-900">{doc.metadata.clientName}</div>
    //                       )}
    //                       <div className="text-xs text-gray-500">{doc.filename}</div>
    //                     </div>
    //                   </div>
    //                 </td>
    //                 <td className="px-6 py-4 whitespace-nowrap">
    //                   <div className="text-sm text-gray-900">{doc.metadata?.referenceNumber || '-'}</div>
    //                 </td>
    //                 <td className="px-6 py-4 whitespace-nowrap">
    //                   <div className="text-sm text-gray-900">{doc.metadata?.date || '-'}</div>
    //                 </td>
    //                 <td className="px-6 py-4 whitespace-nowrap">
    //                   <div className="text-sm text-gray-900">{doc.metadata?.remitent || '-'}</div>
    //                 </td>
    //                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
    //                   {doc.metadata?.amount ? `$${doc.metadata.amount}` : '-'}
    //                 </td>
    //                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
    //                   {doc.metadata?.originCountry || '-'}
    //                 </td>
    //                 <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
    //                   <div className="flex items-center space-x-3 justify-end">
    //                     <button
    //                       onClick={() => setSelectedDocument(doc)}
    //                       className="text-indigo-600 hover:text-indigo-900"
    //                     >
    //                       <EyeIcon className="h-5 w-5" />
    //                     </button>
    //                     <button
    //                       onClick={() => handleDelete(doc.id, doc.storageRef)}
    //                       className="text-red-600 hover:text-red-900"
    //                     >
    //                       <TrashIcon className="h-5 w-5" />
    //                     </button>
    //                   </div>
    //                 </td>
    //               </tr>
    //             ))}
    //           </tbody>
    //         </table>
    //       </div>
    //     </div>
    //   )}

    //   {selectedDocument && selectedDocument.url && (
    //     <PDFViewer
    //       url={selectedDocument.url}
    //       filename={selectedDocument.filename}
    //       isOpen={true}
    //       onClose={() => setSelectedDocument(null)}
    //     />
    //   )}
    // </div>
  );
};

export default DocumentList;
